<script lang="ts">
  /**
   * ScoresTiles — the "Your scores" tile grid, moved unchanged out of the old
   * Scores tab into the scores drawer (tabs slice, 2026-09-10; lane default:
   * Scott named the top-right ring "the scores").
   *
   * HARD RULE: every tile is a fact someone could recount by hand; no
   * indices, no 0-100. Coverage honesty: lap-fact tiles compute over the
   * swimFacts cache (nothing here triggers a re-derivation), and partial
   * coverage is labeled, never passed off as all-time truth.
   */
  import type { WorkoutSummary } from "@contract/types";
  import { medianDurationByStroke } from "../lib/recordEligibility";
  import {
    getAllSwimFacts,
    fastestLengthEver,
    mostLengthsBeforeFirstRest,
    farthestSwim,
    totalLengths,
    longestFlyStretch,
    factsCoverage,
  } from "../lib/allTimeAggregates";
  import { fmtSeconds1 } from "../lib/format";
  import { STROKE_COLORS } from "../lib/designTokens";

  let { workouts }: { workouts: WorkoutSummary[] } = $props();

  // A localStorage read, not a store: re-read when the workout list changes.
  let facts = $derived.by(() => {
    void workouts;
    return getAllSwimFacts();
  });
  let medians = $derived(medianDurationByStroke(facts));
  let fastest = $derived(fastestLengthEver(facts, medians));
  let beforeRest = $derived(mostLengthsBeforeFirstRest(facts));
  let farthest = $derived(farthestSwim(workouts));
  let totals = $derived(totalLengths(facts));
  let fly = $derived(longestFlyStretch(facts));
  let coverage = $derived(factsCoverage(facts, workouts.length));

  // "Jun 8" from a fact date ("YYYY-MM-DD", parsed as LOCAL so the day never
  // shifts across the UTC boundary) or a full ISO timestamp.
  function fmtFactDay(isoDay: string): string {
    const [y, m, d] = isoDay.split("-").map(Number);
    return new Date(y, m - 1, d).toLocaleDateString(undefined, {
      month: "short",
      day: "numeric",
    });
  }
  function fmtStampDay(iso: string): string {
    return new Date(iso).toLocaleDateString(undefined, {
      month: "short",
      day: "numeric",
    });
  }
</script>

<h2 class="sec-label">Your scores</h2>
<div class="grid">
  <!-- Fastest length ever -->
  <div class="tile">
    <div class="k">Fastest length</div>
    {#if fastest}
      <div class="v">{fmtSeconds1(fastest.seconds)}<span class="u">s</span></div>
      <div class="foot">
        {#if fastest.stroke}
          <span
            class="swatch"
            style:background={STROKE_COLORS[fastest.stroke] ?? STROKE_COLORS["Unknown"]}
            aria-hidden="true"
          ></span>{fastest.stroke}<span class="pt"> · {fmtFactDay(fastest.swimDate)}</span>
        {:else}
          <span class="pt">{fmtFactDay(fastest.swimDate)}</span>
        {/if}
      </div>
    {:else}
      <div class="v v-empty">—</div>
      <div class="foot"><span class="pt">no lengths counted yet</span></div>
    {/if}
  </div>

  <!-- Most lengths before first rest. Label shortened to one line so the
       row's tiles stay level (persona gate 2026-07-10 polish). -->
  <div class="tile">
    <div class="k">Before first rest</div>
    {#if beforeRest}
      <div class="v">{beforeRest.count}</div>
      <div class="foot"><span class="pt">lengths · {fmtFactDay(beforeRest.swimDate)}</span></div>
    {:else}
      <div class="v v-empty">—</div>
      <div class="foot"><span class="pt">no lengths counted yet</span></div>
    {/if}
  </div>

  <!-- Farthest swim — from the plain workout list, no lap data needed -->
  <div class="tile">
    <div class="k">Farthest swim</div>
    {#if farthest}
      <div class="v">{Math.round(farthest.meters).toLocaleString()}<span class="u">m</span></div>
      <div class="foot"><span class="pt">{fmtStampDay(farthest.startDate)}</span></div>
    {:else}
      <div class="v v-empty">—</div>
      <div class="foot"><span class="pt">no swims yet</span></div>
    {/if}
  </div>

  <!-- Total lengths — labeled honestly when coverage is partial -->
  <div class="tile">
    <div class="k">Total lengths</div>
    {#if totals.swimCount > 0}
      <div class="v">{totals.total.toLocaleString()}</div>
      <div class="foot">
        <span class="pt">{coverage.complete ? "all time" : `across ${totals.swimCount} swims so far`}</span>
      </div>
    {:else}
      <div class="v v-empty">—</div>
      <div class="foot"><span class="pt">no lengths counted yet</span></div>
    {/if}
  </div>

  <!-- Longest fly stretch — a FACT like every other tile (B3, persona gate
       2026-07-10). Shows the actual longest consecutive fly run; "—" only when
       no fly has ever been counted. -->
  <div class="tile">
    <div class="k">Longest fly stretch</div>
    {#if fly && fly.count > 0}
      <div class="v">{fly.count}</div>
      <div class="foot">
        <span class="pt">{fly.count === 1 ? "length" : "lengths in a row"}{fly.swimDate ? ` · ${fmtFactDay(fly.swimDate)}` : ""}</span>
      </div>
    {:else}
      <div class="v v-empty">—</div>
      <div class="foot"><span class="pt">no fly lengths yet</span></div>
    {/if}
  </div>
</div>

{#if !coverage.complete}
  <p class="coverage-note">Counted from {coverage.counted} of {coverage.total} swims so far.</p>
{/if}

<style>
  /* Section label: the card-title tier (12px uppercase), structural not loud. */
  .sec-label {
    font: var(--type-title-card);
    letter-spacing: 0.08em;
    text-transform: uppercase;
    color: var(--text-muted);
    margin: var(--space-2) 2px calc(-1 * var(--space-1));
  }
  .grid {
    display: grid;
    grid-template-columns: 1fr 1fr;
    /* Equal-height rows — a wrapped label must never make its neighbor tile
       shorter (persona gate 2026-07-10 polish: ragged rows read as a bug). */
    grid-auto-rows: 1fr;
    gap: var(--space-3);
  }
  .tile {
    background: var(--bg-card);
    border: 0;
    border-radius: var(--radius-card);
    padding: var(--space-4) var(--space-4) var(--space-3);
    min-height: 104px;
    display: flex;
    flex-direction: column;
    justify-content: space-between;
    gap: var(--space-2);
    backdrop-filter: blur(22px) saturate(1.2);
    -webkit-backdrop-filter: blur(22px) saturate(1.2);
  }
  /* Five tiles in two columns: the odd last tile spans the row. */
  .tile:last-child:nth-child(odd) {
    grid-column: 1 / -1;
  }
  .k {
    font: var(--type-title-card);
    letter-spacing: 0.05em;
    text-transform: uppercase;
    color: var(--text-muted);
  }
  .v {
    font-size: 31px;
    font-weight: 600;
    line-height: 1;
    letter-spacing: -0.01em;
    color: var(--text-primary);
    font-variant-numeric: tabular-nums;
    display: flex;
    align-items: baseline;
    gap: 2px;
  }
  .v .u {
    font-size: 15px;
    font-weight: 500;
    color: var(--text-body);
    letter-spacing: 0;
  }
  .v-empty {
    color: var(--text-muted);
  }
  .foot {
    font-size: 13px;
    color: var(--text-body);
    line-height: 1.3;
    display: flex;
    align-items: center;
    gap: 5px;
  }
  .foot .pt {
    color: var(--text-muted);
  }
  /* Round stroke dot — matches the stroke-mix legend's dot vocabulary. */
  .swatch {
    width: 10px;
    height: 10px;
    border-radius: var(--radius-pill);
    flex-shrink: 0;
  }
  .coverage-note {
    font-size: 14px;
    color: var(--text-muted);
    margin: calc(-1 * var(--space-2)) 2px 0;
    line-height: 1.35;
  }
</style>
